<template>
    <div class="member-set">
        <ul v-if="row.read(record).length > 0" class="member-set__list">
            <li v-for="entry in row.read(record)" :key="entry[row.idKey]" class="member-set__row">
                <span class="member-set__name">{{ entry.name }}</span>

                <label v-if="row.toggle && editing" class="member-set__toggle">
                    <ToggleSwitch
                        :model-value="row.toggle.reads(entry)"
                        :disabled="busy === entry[row.idKey]"
                        @update:model-value="(next) => toggle(entry, next)"
                    />
                    <small>{{ row.toggle.label }}</small>
                </label>
                <small
                    v-else-if="row.toggle && row.toggle.reads(entry)"
                    class="member-set__flag"
                >{{ row.toggle.label }}</small>

                <Button
                    v-if="editing"
                    icon="pi pi-times"
                    severity="secondary"
                    variant="text"
                    rounded
                    size="small"
                    :disabled="busy === entry[row.idKey]"
                    :aria-label="`Remove ${entry.name}`"
                    @click="remove(entry)"
                />
            </li>
        </ul>
        <p v-else class="member-set__empty">
            None. <template v-if="!editing">Edit to add one.</template>
        </p>

        <div v-if="editing" class="member-set__add">
            <DocumentPicker
                :key="`add-${row.read(record).length}`"
                :client="row.client"
                :model-value="picked"
                :placeholder="row.addLabel"
                @update:model-value="(value) => (picked = value)"
            />
            <Button
                :label="row.addLabel"
                icon="pi pi-plus"
                size="small"
                severity="secondary"
                :disabled="picked === null || busy !== ''"
                @click="add"
            />
        </div>

        <p v-if="failure" class="member-set__failure">
            {{ failureText(failure) }}
        </p>
    </div>
</template>

<script setup>
import Button from 'primevue/button'
import ToggleSwitch from 'primevue/toggleswitch'
import { ref } from 'vue'

import { failureText } from '@/lib/failureText.js'
import DocumentPicker from './DocumentPicker.vue'

// A set of documents, each member added and removed by its own request.
//
// There is no list to send back: the server addresses a member entirely in the
// path, so every change here is one call and every refusal is about one member.
// That is why nothing is drafted — a Save button would be collecting edits the
// server has already been told about one at a time.
const props = defineProps({
    row: { type: Object, required: true },
    record: { type: Object, required: true },
    store: { type: Object, required: true },
    // False draws the members without the actions that change them. Each change
    // here is its own request, so a stray click is a write nobody asked for.
    editing: { type: Boolean, default: false }
})
const emit = defineEmits(['changed'])

const busy = ref('')
const failure = ref(null)
const picked = ref(null)

// `changed` only on success. The parent bumps a version key on that event and
// the key remounts this component, which destroys the `failure` ref that had
// just caught the refusal — so reporting a refusal and announcing it in the
// same breath meant never reporting one at all.
async function run(memberId, work) {
    busy.value = memberId
    failure.value = null
    const ok = await work()
    busy.value = ''
    if (ok === false) {
        failure.value = props.store.error
        return false
    }
    emit('changed')
    return true
}

async function add() {
    if (picked.value === null) {
        return
    }
    const chosen = picked.value
    // Cleared after the server takes it, not before. A refused add used to
    // empty the box as well, so the reply named a member nobody could see.
    const ok = await run(chosen, () => props.row.add(props.store, props.record._id, chosen))
    if (ok === true) {
        picked.value = null
    }
}

function remove(entry) {
    const id = entry[props.row.idKey]
    return run(id, () => props.row.remove(props.store, props.record._id, id))
}

function toggle(entry, next) {
    const id = entry[props.row.idKey]
    const action = next ? props.row.toggle.on : props.row.toggle.off
    return run(id, () => action(props.store, props.record._id, id))
}
</script>

<style scoped>
.member-set__list {
    list-style: none;
    margin: 0 0 8px;
    padding: 0;
}

.member-set__row {
    align-items: center;
    border-bottom: 1px solid var(--p-content-border-color);
    display: flex;
    gap: 8px;
    padding: 6px 0;
}

.member-set__name {
    flex: 1 1 auto;
    font-size: 13px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.member-set__toggle {
    align-items: center;
    display: flex;
    gap: 6px;
}

.member-set__toggle small {
    color: var(--p-surface-400);
    font-size: 11px;
}

.member-set__flag {
    color: var(--p-surface-400);
    font-size: 11px;
}

.member-set__empty {
    color: var(--p-surface-400);
    font-size: 12px;
    margin: 0 0 8px;
}

.member-set__add {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.member-set__failure {
    color: var(--p-status-danger);
    font-size: 12px;
    margin: 6px 0 0;
}
</style>
